import React, {
    FunctionComponent,
    useState,
    ChangeEvent,
    useEffect,
} from 'react'
import { useTranslation } from 'react-i18next'
import some from 'lodash/some'
import { depositAlternativeOptions, Step3Props } from './types'
import { Step3ValueModel } from '../types'
import {
    validateDepositAlternative,
    validateStep3,
    validateTeamName,
} from '../Validation'
import {
    validateMunicipality,
    validateVisitingAddressStreetAddress,
    validateVisitingAddressZipCode,
    validateVisitingAddressCity,
    validatePostalAddressStreetBox,
    validatePostalAddressZipCode,
    validatePostalAddressCity,
    validateDeliveryAddressStreetAddress,
    validateDeliveryAddressZipCode,
    validateDeliveryAddressCity,
} from '../../shared/Validation';
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import { getAllErrors, removeAllErrorsForKey } from "@sharedComponents/PantaForm/utils";
import InputText from "@sharedComponents/InputText";
import InputSelect from "@sharedComponents/InputSelect";
import InputToggleButton from "@sharedComponents/InputToggleButton";
import { IHeard, IMunicipality } from "@apps/Shared/Services/CompanyRegistrations/interfaces";
import InputCheckbox from "@sharedComponents/InputCheckbox";
import InputTextArea from "@sharedComponents/InputTextArea";
import { InputSelectOption } from "@sharedComponents/InputSelect/types";
import Panel from "@sharedComponents/Panel";
import { PanelType } from "@sharedComponents/Panel/Panel";
import InlineLink from "@sharedComponents/Links/InlineLink";
import { LinkType } from "@sharedComponents/Links/types";
import Button from "@sharedComponents/Button";
import {ButtonType} from '@sharedComponents/Button/types'
import Icon from "@sharedComponents/Icon";
import Alert from "@sharedComponents/Alert";

const Step3: FunctionComponent<Step3Props> = props => {
    const { t } = useTranslation()

    const [formErrors, setFormErrors] = useState<FormErrorType[]>([])

    const allErrors = getAllErrors(formErrors)

    const [stepValues, setStepValues] = useState<Step3ValueModel>({
        depositAlternative: '',
        visitingAddress: {},
        postalAddress: {},
        deliveryAddress: {},
    })

    const rehydrate = (values: Step3ValueModel) => {
        if (values) {
            setStepValues({
                ...stepValues,
                teamName: values.teamName || '',
                municipality: values.municipality,
                depositAlternative: values.depositAlternative || '',
                visitingAddress: values.visitingAddress || {},
                postalAddress: values.postalAddress || {},
                deliveryAddress: values.deliveryAddress || {},
                message: values.message || '',
                heard: values.heard,
                cardboardContainer: values.cardboardContainer,
                floorballContainer: values.floorballContainer,
            } as Step3ValueModel)
        }
    }

    useEffect(() => {
        rehydrate(props.values)
    }, [props.rehydrateFormKey])

    useEffect(() => {
        rehydrate(props.values)
    }, [])

    const onChangeTeamName = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setStepValues({ ...stepValues, teamName: value })
        props.onChangeValues && props.onChangeValues({ teamName: value })
        setFormErrors(validateTeamName(value, formErrors))
    }

    const onChangeMunicipality = (option: InputSelectOption) => {
        const value = option
        setStepValues({ ...stepValues, municipality: value })
        props.onChangeValues && props.onChangeValues({ municipality: value })
        setFormErrors(validateMunicipality(value, formErrors))
    }

    const onChangeDepositAlternative = (value: string) => {
        setStepValues({ ...stepValues, depositAlternative: value })
        props.onChangeValues &&
            props.onChangeValues({ depositAlternative: value })
        setFormErrors(validateDepositAlternative(value, formErrors))
    }

    const onChangeVisitingAddressStreetAddress = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value

        setStepValues({
            ...stepValues,
            visitingAddress: {
                ...stepValues.visitingAddress,
                streetAddress: value,
            },
        })

        props.onChangeValues &&
            props.onChangeValues({
                visitingAddress: {
                    ...stepValues.visitingAddress,
                    streetAddress: value,
                },
            })

        setFormErrors(validateVisitingAddressStreetAddress(value, formErrors))
    }

    const onChangeVisitingAddressZipCode = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        let value = e.target.value
        if (value.length > 4) {
            value = value.trimEnd()
        }

        const visitingAddress = {
            ...stepValues.visitingAddress,
            zipCode: value,
        }

        setStepValues({
            ...stepValues,
            visitingAddress,
        })

        props.onChangeValues && props.onChangeValues({ visitingAddress })
        
        setFormErrors(
            validateVisitingAddressZipCode(
                value,
                true,
                formErrors
            )
        )
    }

    const onChangeVisitingAddressCity = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        const visitingAddress = {
            ...stepValues.visitingAddress,
            city: value,
        }

        setStepValues({
            ...stepValues,
            visitingAddress,
        })

        props.onChangeValues && props.onChangeValues({ visitingAddress })
        setFormErrors(validateVisitingAddressCity(value, formErrors))
    }

    const onChangeVisitingAddressIsAlsoPostalAddress = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.checked

        const visitingAddress = {
            ...stepValues.visitingAddress,
            isAlsoPostalAddress: value,
        }

        const postalAddress =
            value === true
                ? { name: stepValues.postalAddress?.name || '' }
                : stepValues.postalAddress
        const deliveryAddress =
            value === true
                ? {
                      name: stepValues.deliveryAddress?.name || '',
                      acceptDeliveryWithoutSigning:
                          stepValues.deliveryAddress
                              ?.acceptDeliveryWithoutSigning,
                  }
                : stepValues.deliveryAddress

        setStepValues({
            ...stepValues,
            visitingAddress,
            postalAddress,
            deliveryAddress,
        })

        props.onChangeValues &&
            props.onChangeValues({
                visitingAddress,
                postalAddress,
                deliveryAddress,
            })

        if (value === true) {
            let errors = removeAllErrorsForKey(
                formErrors,
                'postalAddress.streetBox'
            )
            errors = removeAllErrorsForKey(errors, 'postalAddress.zipCode')
            errors = removeAllErrorsForKey(errors, 'postalAddress.city')
            errors = removeAllErrorsForKey(
                errors,
                'deliveryAddress.streetAddress'
            )
            errors = removeAllErrorsForKey(errors, 'deliveryAddress.zipCode')
            errors = removeAllErrorsForKey(errors, 'deliveryAddress.city')

            setFormErrors(errors)
        }
    }

    const onChangePostalAddressName = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        const postalAddress = {
            ...stepValues.postalAddress,
            name: value,
        }

        setStepValues({ ...stepValues, postalAddress })
        props.onChangeValues && props.onChangeValues({ postalAddress })
    }

    const onChangePostalAddressStreetBox = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value

        const postalAddress = {
            ...stepValues.postalAddress,
            streetBox: value,
        }

        setStepValues({ ...stepValues, postalAddress })
        props.onChangeValues && props.onChangeValues({ postalAddress })
        setFormErrors(
            validatePostalAddressStreetBox(
                value,
                !!stepValues.visitingAddress.isAlsoPostalAddress,
                formErrors
            )
        )
    }

    const onChangePostalAddressZipCode = (e: ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value
        if (value.length > 4) {
            value = value.trimEnd()
        }

        const postalAddress = {
            ...stepValues.postalAddress,
            zipCode: value,
        }

        setStepValues({ ...stepValues, postalAddress })
        props.onChangeValues && props.onChangeValues({ postalAddress })

        setFormErrors(
            validatePostalAddressZipCode(
                value,
                !stepValues.visitingAddress.isAlsoPostalAddress,
                true,
                formErrors
            )
        )
    }

    const onChangePostalAddressCity = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        const postalAddress = {
            ...stepValues.postalAddress,
            city: value,
        }

        setStepValues({ ...stepValues, postalAddress })
        props.onChangeValues && props.onChangeValues({ postalAddress })
        setFormErrors(
            validatePostalAddressCity(
                value,
                !!stepValues.visitingAddress.isAlsoPostalAddress,
                formErrors
            )
        )
    }

    const onChangeDeliveryAddressName = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        const deliveryAddress = {
            ...stepValues.deliveryAddress,
            name: value,
        }

        setStepValues({ ...stepValues, deliveryAddress })
        props.onChangeValues && props.onChangeValues({ deliveryAddress })
    }

    const onChangeDeliveryAddressStreetAddress = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value

        setStepValues({
            ...stepValues,
            deliveryAddress: {
                ...stepValues.deliveryAddress,
                streetAddress: value,
            },
        })

        props.onChangeValues &&
            props.onChangeValues({
                deliveryAddress: {
                    ...stepValues.deliveryAddress,
                    streetAddress: value,
                },
            })

        setFormErrors(
            validateDeliveryAddressStreetAddress(
                value,
                !!stepValues.visitingAddress.isAlsoPostalAddress,
                formErrors
            )
        )
    }

    const onChangeDeliveryAddressZipCode = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        let value = e.target.value
        if (value.length > 4) {
            value = value.trimEnd()
        }

        const deliveryAddress = {
            ...stepValues.deliveryAddress,
            zipCode: value,
        }

        setStepValues({
            ...stepValues,
            deliveryAddress,
        })

        props.onChangeValues && props.onChangeValues({ deliveryAddress })

        setFormErrors(
            validateDeliveryAddressZipCode(
                value,
                !stepValues.visitingAddress.isAlsoPostalAddress,
                true,
                formErrors
            )
        )
    }

    const onChangeDeliveryAddressCity = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        const deliveryAddress = {
            ...stepValues.deliveryAddress,
            city: value,
        }

        setStepValues({
            ...stepValues,
            deliveryAddress,
        })

        props.onChangeValues && props.onChangeValues({ deliveryAddress })
        setFormErrors(
            validateDeliveryAddressCity(
                value,
                !!stepValues.visitingAddress.isAlsoPostalAddress,
                formErrors
            )
        )
    }

    const onChangeDeliveryAddressAcceptDeliveryWithoutSigning = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.checked

        const deliveryAddress = {
            ...stepValues.deliveryAddress,
            acceptDeliveryWithoutSigning: value,
        }

        setStepValues({
            ...stepValues,
            deliveryAddress,
        })

        props.onChangeValues && props.onChangeValues({ deliveryAddress })
    }

    const onChangeMessage = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value

        setStepValues({
            ...stepValues,
            message: value,
        })

        props.onChangeValues && props.onChangeValues({ message: value })
    }

    const onChangeHeard = (option: InputSelectOption) => {
        const value = option
        setStepValues({ ...stepValues, heard: value })
        props.onChangeValues && props.onChangeValues({ heard: value })
    }

    const onChangeCardboardContainer = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.checked

        setStepValues({
            ...stepValues,
            cardboardContainer: value,
        })

        props.onChangeValues &&
            props.onChangeValues({ cardboardContainer: value })
    }

    const onChangeFloorballContainer = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.checked

        setStepValues({
            ...stepValues,
            floorballContainer: value,
        })

        props.onChangeValues &&
            props.onChangeValues({ floorballContainer: value })
    }

    const nextStep = () => {
        validateStep3(stepValues, formErrors).then(
            (errors: FormErrorType[]) => {
                if (errors && errors.length > 0) {
                    setFormErrors(errors)
                } else {
                    setFormErrors([])
                    props.onChangeValues && props.onChangeValues({})
                    props.nextStep && props.nextStep()
                }
            }
        )
    }

    const prevStep = () => {
        props.prevStep && props.prevStep()
    }

    const onCancel = () => {
        setFormErrors([])

        props.onCancel && props.onCancel()
    }

    const uniqueErrors = (key: string) => {
        const errors = formErrors.find(err => err.key === key);
        const uniqueErr = errors ? [...new Set(errors.messages)] : [];

        return uniqueErr.length > 0 ? uniqueErr.join(', ') : '';
    }

    return (
        <div className='form-step-wrapper form-step-wrapper--wide'>
            <h3>
                {t('companyRegistration.customer.association.stepLabel.step3')}
            </h3>
            <p className="required-asterisk mt-3">{t('global.requiredAsterisk')}</p>

            {props.cmsContent ? (
                <div className='form-step-wrapper__content'>
                    {props.cmsContent}
                </div>
            ) : (
                ''
            )}
            <div className='fields-wrapper'>
                <div className='fields-wrapper__field'>
                    <InputText
                        id="association-name"
                        label={t(
                            'companyRegistration.customer.association.teamName'
                        )}
                        value={stepValues.teamName || ''}
                        hasError={!!some(formErrors, ['key', 'teamName'])}
                        required={true}
                        onChange={onChangeTeamName}
                        maxLength={50}
                        errorText={uniqueErrors('teamName')}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputSelect
                        id="municipality"
                        emptyMessage={t('global.choose')}
                        hideLabel={false}
                        label={t('companyRegistration.customer.municipality')}
                        value={stepValues.municipality}
                        onChange={onChangeMunicipality}
                        hasError={!!some(formErrors, ['key', 'municipality'])}
                        options={props.municipalities.map(
                            (heard: IMunicipality) => {
                                return {
                                    label: heard.description,
                                    value: heard.code + '',
                                } as InputSelectOption
                            }
                        )}
                        required={true}
                        disabled={props.municipalities.length === 0}
                        errorText={uniqueErrors('municipality')}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <InputToggleButton
                        id="deposit-alternative"
                        className="toggle-field"
                        hideLabel={false}
                        label={t(
                            'companyRegistration.customer.depositAlternative'
                        )}
                        required={true}
                        hasError={
                            !!some(formErrors, ['key', 'depositAlternative'])
                        }
                        value={stepValues.depositAlternative || ''}
                        onSelect={onChangeDepositAlternative}
                        options={depositAlternativeOptions(t)}
                        errorText={uniqueErrors('depositAlternative')}
                    />
                </div>
                <div className='fields-wrapper__field'>
                    <Panel type={PanelType.Info} compact={true}>
                        <span className='field-info'>
                            {t(
                                'companyRegistration.customer.depositAlternativeInfo'
                            )}
                            <InlineLink
                                hideIcon={true}
                                url={t(
                                    'companyRegistration.customer.depositAlternativeInfoLink'
                                )}
                                text={t(
                                    'companyRegistration.customer.depositAlternativeInfoLinkText'
                                )}
                                type={LinkType.Extern}
                            />
                        </span>
                    </Panel>
                </div>
                {
                    <>
                        <h5 className='fields-wrapper__title'>
                            {t('companyRegistration.customer.visitingAddress')}
                        </h5>
                        <div className='fields-wrapper__field'>
                            <InputText
                                id="visiting-address-street-address"
                                label={t('companyRegistration.streetAddress')}
                                value={
                                    stepValues.visitingAddress.streetAddress ||
                                    ''
                                }
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'visitingAddress.streetAddress',
                                    ])
                                }
                                onChange={onChangeVisitingAddressStreetAddress}
                                required={true}
                                maxLength={100}
                                errorText={uniqueErrors('visitingAddress.streetAddress')}
                            />
                        </div>
                        <div className='fields-wrapper__field'>
                            <InputText
                                id="visiting-address-zip-code"
                                label={t('companyRegistration.zipCode')}
                                value={stepValues.visitingAddress.zipCode || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'visitingAddress.zipCode',
                                    ])
                                }
                                onChange={onChangeVisitingAddressZipCode}
                                required={true}
                                errorText={uniqueErrors('visitingAddress.zipCode')}
                            />
                        </div>
                        <div className='fields-wrapper__field'>
                            <InputText
                                id="visiting-address-city"
                                label={t('companyRegistration.city')}
                                value={stepValues.visitingAddress.city || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'visitingAddress.city',
                                    ])
                                }
                                onChange={onChangeVisitingAddressCity}
                                required={true}
                                maxLength={50}
                                errorText={uniqueErrors('visitingAddress.city')}
                            />
                        </div>
                        <div className='fields-wrapper__field'>
                            <InputCheckbox
                                id="visiting-address-is-also-postal-address"
                                label={t(
                                    'companyRegistration.customer.visitingAddressIsAlsoPostalAddress'
                                )}
                                onChange={
                                    onChangeVisitingAddressIsAlsoPostalAddress
                                }
                                checked={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress || false
                                }
                            />
                        </div>
                    </>
                }
                {
                    <>
                        <h5 className={`fields-wrapper__title ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__title--disabled' : ''}`}>
                            {t('companyRegistration.customer.postalAddress')}
                        </h5>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <Panel type={PanelType.Info} compact={true}>
                                <span className='field-info'>
                                    {t(
                                        'companyRegistration.customer.postalAddressInfo'
                                    )}
                                </span>
                            </Panel>
                        </div>
                        <div className='fields-wrapper__field'>
                            <InputText
                                id="postal-address-name"
                                label={t(
                                    'companyRegistration.customer.nameWithInfo'
                                )}
                                value={stepValues.postalAddress.name || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'postalAddress.name',
                                    ])
                                }
                                onChange={onChangePostalAddressName}
                                required={false}
                                maxLength={50}
                            />
                        </div>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <InputText
                                id="postal-address-street-box"
                                label={t(
                                    'companyRegistration.customer.streetBox'
                                )}
                                value={stepValues.postalAddress.streetBox || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'postalAddress.streetBox',
                                    ])
                                }
                                onChange={onChangePostalAddressStreetBox}
                                required={true}
                                disabled={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress
                                }
                                maxLength={100}
                                errorText={uniqueErrors('postalAddress.streetBox')}
                            />
                        </div>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <InputText
                                id="postal-address-zip-code"
                                label={t('companyRegistration.zipCode')}
                                value={stepValues.postalAddress.zipCode || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'postalAddress.zipCode',
                                    ])
                                }
                                onChange={onChangePostalAddressZipCode}
                                required={true}
                                disabled={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress
                                }
                                errorText={uniqueErrors('postalAddress.zipCode')}
                            />
                        </div>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <InputText
                                id="postal-address-city"
                                label={t('companyRegistration.city')}
                                value={stepValues.postalAddress.city || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'postalAddress.city',
                                    ])
                                }
                                onChange={onChangePostalAddressCity}
                                required={true}
                                disabled={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress
                                }
                                maxLength={50}
                                errorText={uniqueErrors('postalAddress.city')}
                            />
                        </div>
                    </>
                }
                {
                    <>
                        <h5 className={`fields-wrapper__title ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__title--disabled' : ''}`}>
                            {t('companyRegistration.customer.deliveryAddress')}
                        </h5>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <Panel type={PanelType.Info} compact={true}>
                                <span className='field-info'>
                                    {t(
                                        'companyRegistration.customer.association.deliveryAddressInfo'
                                    )}
                                </span>
                            </Panel>
                        </div>
                        <div className='fields-wrapper__field'>
                            <InputText
                                id="delivery-address-name"
                                label={t(
                                    'companyRegistration.customer.nameWithInfo'
                                )}
                                value={stepValues.deliveryAddress?.name || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'deliveryAddress.name',
                                    ])
                                }
                                onChange={onChangeDeliveryAddressName}
                                required={false}
                                maxLength={50}
                            />
                        </div>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <InputText
                                id="delivery-address-street-address"
                                label={t('companyRegistration.streetAddress')}
                                value={
                                    stepValues.deliveryAddress?.streetAddress ||
                                    ''
                                }
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'deliveryAddress.streetAddress',
                                    ])
                                }
                                onChange={onChangeDeliveryAddressStreetAddress}
                                required={true}
                                disabled={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress
                                }
                                autoComplete="street-address"
                                maxLength={100}
                                errorText={uniqueErrors('deliveryAddress.streetAddress')}
                            />
                        </div>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <InputText
                                id="delivery-address-zip-code"
                                label={t('companyRegistration.zipCode')}
                                value={
                                    stepValues.deliveryAddress?.zipCode || ''
                                }
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'deliveryAddress.zipCode',
                                    ])
                                }
                                onChange={onChangeDeliveryAddressZipCode}
                                required={true}
                                disabled={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress
                                }
                                autoComplete="postal-code"
                                errorText={uniqueErrors('deliveryAddress.zipCode')}
                            />
                        </div>
                        <div className={`fields-wrapper__field ${stepValues.visitingAddress.isAlsoPostalAddress ? 'fields-wrapper__field--disabled' : ''}`}>
                            <InputText
                                id="delivery-address-city"
                                label={t('companyRegistration.city')}
                                value={stepValues.deliveryAddress?.city || ''}
                                hasError={
                                    !!some(formErrors, [
                                        'key',
                                        'deliveryAddress.city',
                                    ])
                                }
                                onChange={onChangeDeliveryAddressCity}
                                required={true}
                                disabled={
                                    stepValues.visitingAddress
                                        .isAlsoPostalAddress
                                }
                                autoComplete="street-address"
                                maxLength={50}
                                errorText={uniqueErrors('deliveryAddress.city')}
                            />
                        </div>
                        <div className='fields-wrapper__field'>
                            <InputCheckbox
                                id="delivery-address-accept-delivery-without-signing"
                                label={t(
                                    'companyRegistration.customer.acceptDeliveryWithoutSigning'
                                )}
                                onChange={
                                    onChangeDeliveryAddressAcceptDeliveryWithoutSigning
                                }
                                checked={
                                    stepValues.deliveryAddress
                                        ?.acceptDeliveryWithoutSigning || false
                                }
                            />
                        </div>
                    </>
                }
                {
                    <>
                        <h5 className='fields-wrapper__title'>
                            {t('companyRegistration.customer.messageForRp')}
                        </h5>
                        <div className='fields-wrapper__field'>
                            <InputTextArea
                                id="message-to-rp"
                                label={t(
                                    'companyRegistration.customer.message'
                                )}
                                value={stepValues.message || ''}
                                hasError={
                                    !!some(formErrors, ['key', 'message'])
                                }
                                onChange={onChangeMessage}
                                maxLength={512}
                            />
                        </div>
                        <div className='fields-wrapper__field'>
                            <InputSelect
                                id="heard"
                                emptyMessage={t('global.choose')}
                                hideLabel={false}
                                label={t(
                                    'companyRegistration.customer.association.howDidYouHearAboutUs'
                                )}
                                value={stepValues.heard}
                                onChange={onChangeHeard}
                                hasError={!!some(formErrors, ['key', 'heard'])}
                                options={props.heards.map(
                                    (municipality: IHeard) => {
                                        return {
                                            label: municipality.description,
                                            value: municipality.id + '',
                                        } as InputSelectOption
                                    }
                                )}
                                disabled={props.heards.length === 0}
                                clearable={true}
                            />
                        </div>
                        <div className='fields-wrapper__field'>
                            <InputCheckbox
                                id="cardboard-container"
                                label={t(
                                    'companyRegistration.customer.association.cardboardContainer'
                                )}
                                onChange={onChangeCardboardContainer}
                                checked={stepValues.cardboardContainer || false}
                            />
                        </div>
                        <div className='fields-wrapper__field'>
                            <InputCheckbox
                                id="floorball-container"
                                label={t(
                                    'companyRegistration.customer.association.floorballContainer'
                                )}
                                onChange={onChangeFloorballContainer}
                                checked={stepValues.floorballContainer || false}
                            />
                        </div>
                    </>
                }
            </div>
            {allErrors.length > 0 && (
                <div className='form-step-wrapper__error'>
                    <Alert
                        type="danger"
                        text={t(
                            'companyRegistration.companyInfoContainsErrors'
                        )}
                        subtext={allErrors}
                        compact={false}
                    />
                </div>
            )}
            {props.unexpectedErrors.length > 0 && (
                <Alert
                    type="danger"
                    text={''}
                    subtext={props.unexpectedErrors}
                    compact={true}
                />
            )}
            <div className='buttons-wrapper'>
                <Button
                    type={ButtonType.PRIMARY}
                    className="next-button"
                    text={
                        <span  className='buttons-wrapper__button-text buttons-wrapper__button-text--next'>
                            <span>{t('button.next')}</span>
                            <Icon iconName="arrow-right" />
                        </span>
                    }
                    onClick={nextStep}
                />
                <Button
                    type={ButtonType.PRIMARY}
                    className="prev-button"
                    text={
                        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--prev'>
                            <Icon iconName="arrow-left" />
                            <span>{t('button.previous')}</span>
                        </span>
                    }
                    onClick={prevStep}
                />
                <Button
                    type={ButtonType.SECONDARY}
                    className="cancel-button"
                    text={
                        <span className='buttons-wrapper__button-text'>
                            <Icon iconName="close" />
                            <span>{t('button.cancel')}</span>
                        </span>
                    }
                    onClick={onCancel}
                />
            </div>
        </div>
    )
}

export default Step3